// Admin Configuration Service
// API client for centralized admin configuration storage

import type { AdminConfig } from '@/types/admin.types';
import { getBackendUrl as getRuntimeBackendUrl } from '@/config/runtimeConfig';
import { getBackendHeaders } from './backendAuth';

// Use dedicated auth database URL (separate from chat storage)
/**
 * Get backend URL with priority:
 * 1. Runtime config (container startup injection - HIGHEST PRIORITY)
 * 2. Build-time env vars (development fallback)
 * 3. AdminContext localStorage (user override for testing only - LOWEST PRIORITY)
 *
 * Security: Prevents mixed content errors by rejecting localhost HTTP URLs
 * when running on HTTPS production sites.
 */
function getBackendUrl(): string {
  const isHttpsSite = typeof window !== 'undefined' && window.location.protocol === 'https:';

  // Priority 1 & 2: Runtime config (container env vars) > Build-time env vars
  const runtimeUrl = getRuntimeBackendUrl();
  if (runtimeUrl) {
    console.log('[adminConfigService] ✅ Using runtime/build-time backend URL:', runtimeUrl);
    return runtimeUrl;
  }

  // Priority 3: AdminContext localStorage (fallback for testing/dev only)
  try {
    const adminConfigStr = localStorage.getItem('stellar_admin_config');
    if (adminConfigStr) {
      const adminConfig = JSON.parse(adminConfigStr);
      const storedUrl = adminConfig.authDatabase?.backendUrl;

      if (storedUrl) {
        // Security check: Don't use localhost HTTP URL on HTTPS production sites
        // This prevents mixed content errors (HTTPS → HTTP blocked by browser)
        const isLocalhostHttp = storedUrl.startsWith('http://localhost') ||
                                 storedUrl.startsWith('http://127.0.0.1');

        if (isHttpsSite && isLocalhostHttp) {
          console.warn(
            '[adminConfigService] ⚠️ Mixed content detected: Cannot use HTTP localhost URL on HTTPS site.',
            '\nStored URL:', storedUrl,
            '\nFalling back to hardcoded default.',
            '\nTo fix: Update backend URL in Admin Configuration → Authentication tab'
          );
          return 'http://localhost:8000/api'; // Development fallback
        } else {
          console.log('[adminConfigService] ⚠️ Using backend URL from localStorage (low priority):', storedUrl);
          return storedUrl;
        }
      }
    }
  } catch (error) {
    console.warn('[adminConfigService] Failed to load backend URL from admin config:', error);
  }

  // Final fallback
  console.warn('[adminConfigService] ⚠️ No backend URL configured, using default');
  return 'http://localhost:8000/api';
}

class AdminConfigAPIService {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    return getBackendHeaders();
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Get admin configuration from backend
   */
  async getConfig(): Promise<AdminConfig> {
    const response = await fetch(`${getBackendUrl()}/admin-config`, {
      headers: await this.getAuthHeaders(),
    });

    return this.handleResponse<AdminConfig>(response);
  }

  /**
   * Update admin configuration (full replace)
   */
  async updateConfig(config: AdminConfig): Promise<AdminConfig> {
    const response = await fetch(`${getBackendUrl()}/admin-config`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(config),
    });

    return this.handleResponse<AdminConfig>(response);
  }

  /**
   * Merge partial configuration (recommended)
   */
  async mergeConfig(partialConfig: Partial<AdminConfig>): Promise<AdminConfig> {
    const response = await fetch(`${getBackendUrl()}/admin-config`, {
      method: 'PATCH',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(partialConfig),
    });

    return this.handleResponse<AdminConfig>(response);
  }

  /**
   * Reset configuration to defaults
   */
  async resetConfig(): Promise<AdminConfig> {
    const response = await fetch(`${getBackendUrl()}/admin-config/reset`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
    });

    return this.handleResponse<AdminConfig>(response);
  }

  /**
   * Check if backend is available
   */
  async isBackendAvailable(): Promise<boolean> {
    try {
      const response = await fetch(`${getBackendUrl().replace('/api', '')}/health`, {
        method: 'GET',
      });
      return response.ok;
    } catch {
      return false;
    }
  }
}

export const adminConfigAPI = new AdminConfigAPIService();
